import { useState } from "react";
import { Button, ButtonVariant, Modal, Show } from "shared/components";
import { getFullName } from "shared/utils/get-full-name";
import { getApplicantRole } from "utils/getApplicantRole";
import { IntroView } from "./slices/IntroView";
import { SelectOptionView } from "./slices/SelectOptionView";
import { Views } from "./types";
import { useCombinedIncomeContext } from "../../providers/useCombinedIncome";
import { AddIncomeSource } from "./slices/AddIncomeSource";
import { AddIncomeSourceFlow } from "./slices/AddIncomeSourceFlow";
import { ReviewNewIncomeSource } from "./slices/ReviewNewIncomeSource";
import { CombinedIncomeStepData } from "shared/app/InviteAndCreateModal/slices/ManualApplication/slices/CombinedIncome/types";
import { toast } from "react-toastify";
import { buildCreateIncomePayload } from "shared/app/InviteAndCreateModal/slices/ManualApplication/slices/CombinedIncome/utils";
import { useMutation } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { getErrorMessage } from "utils/error";
import { SuccessModal } from "../ReverifyIncomeModal/slices/components/SuccessModal";
import { IncomeStatus } from "shared/types";

interface Props {
  opened: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const RerunIncomeModal = ({ opened, onClose, onSuccess }: Props) => {
  const [view, setView] = useState<Views>(Views.SelectOption);
  const [selectedOption, setSelectedOption] = useState<Views>(Views.Intro);
  const [newIncomeDraft, setNewIncomeDraft] = useState<CombinedIncomeStepData>(
    {}
  );
  const {
    rerun,
    isRerunning,
    applicant,
    verificationData,
    applicationMeta,
    activeVerification,
    refetch,
    defaultCombinedIncomeVerificationSchema,
    incomes
  } = useCombinedIncomeContext();

  // Mirrors the API's income_being_processed guard on a new report. Adding an income source stays open:
  // the API allows it, and a re-verify or a just-added income can hold an income in processing a while.
  const rerunBlockedReason = incomes.some(
    (income) => income.status === IncomeStatus.Processing
  )
    ? "Not available while an income is still being verified"
    : undefined;
  // "Run new report" is the default pick, so fall back to the option that is still available.
  const effectiveOption =
    rerunBlockedReason && selectedOption === Views.Intro
      ? Views.AddIncomeSource
      : selectedOption;

  const applicationId = applicationMeta.data?.id;
  const reVerificationFeeCents =
    verificationData?.combined_income_re_verification_fee?.cents ?? 0;

  const addManualIncomeMutation = useMutation({
    mutationKey: [
      "ADD_MANUAL_INCOME",
      "CREATE",
      applicationId,
      activeVerification?.id
    ],
    mutationFn: (draft: CombinedIncomeStepData) =>
      instance.post(
        `/portal/v1/applications/${applicationId}/verifications/${activeVerification?.id}/income/add_manual?applicant_id=${applicant.id}`,
        {
          ...buildCreateIncomePayload(draft, draft),
          income_source_type: draft.income_source_type,
          documents: draft.documents
        }
      ),
    onSuccess: () => {
      refetch();
      setView(Views.SuccessfullyAddedNewIncome);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error) || "Failed to add income");
    }
  });

  const applicantFullName = getFullName({
    firstName: applicant.first_name,
    middleName: applicant.middle_name,
    lastName: applicant.last_name,
    short: true
  });

  const priceCents =
    defaultCombinedIncomeVerificationSchema?.screening_fee.cents || 0;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName="max-w-[488px] w-full flex flex-col"
    >
      <div className="flex-grow min-h-0">
        <Show if={view === Views.SelectOption}>
          <SelectOptionView
            onClose={onClose}
            costCents={reVerificationFeeCents}
            onContinue={() => setView(effectiveOption)}
            selectedOption={effectiveOption}
            setSelectedOption={setSelectedOption}
            rerunBlockedReason={rerunBlockedReason}
          />
        </Show>

        <Show if={view === Views.Intro}>
          <IntroView
            onClose={onClose}
            applicantFullName={applicantFullName}
            applicantRole={getApplicantRole(applicant.role)}
            email={applicant.email}
            phone={applicant.phone}
            cents={priceCents}
          />
        </Show>

        <Show if={view === Views.RequestSent}>
          <SuccessModal
            onClose={onClose}
            text="Request has been sent to the applicant"
          />
        </Show>

        <Show if={view === Views.AddIncomeSource}>
          <AddIncomeSource
            onClose={onClose}
            onContinue={() => setView(Views.CombinedIncomeFlow)}
            onBack={() => setView(Views.SelectOption)}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
          />
        </Show>

        <Show if={view === Views.CombinedIncomeFlow}>
          <AddIncomeSourceFlow
            applicantId={applicant.id}
            applicationId={applicationMeta.data?.id as number}
            verification={activeVerification}
            refetch={refetch}
            initialStepData={newIncomeDraft}
            onBack={() => setView(Views.AddIncomeSource)}
            navigateToReview={(draft) => {
              setNewIncomeDraft(draft);
              setView(Views.ReviewNewIncomeSource);
            }}
            onClose={onClose}
          />
        </Show>

        <Show if={view === Views.ReviewNewIncomeSource}>
          <ReviewNewIncomeSource
            newIncomeData={newIncomeDraft}
            onClose={onClose}
            onBack={() => setView(Views.CombinedIncomeFlow)}
            onSubmit={() => addManualIncomeMutation.mutate(newIncomeDraft)}
            isSubmitting={addManualIncomeMutation.isPending}
            cents={reVerificationFeeCents}
          />
        </Show>

        <Show if={view === Views.SuccessfullyAddedNewIncome}>
          <SuccessModal
            onClose={onClose}
            text="Documents submitted for verification"
            textClassName="my-2"
          />
        </Show>
      </div>

      <Show if={view === Views.Intro}>
        <div className="px-6 py-4 border-t border-alpha-100 gap-2 flex justify-end self-end w-full">
          <Button className="w-[125px]" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            className="w-[160px]"
            loading={isRerunning}
            onClick={() =>
              rerun(undefined, {
                onSuccess: () => {
                  setView(Views.RequestSent);
                  onSuccess?.();
                }
              })
            }
          >
            Send request
          </Button>
        </div>
      </Show>
    </Modal>
  );
};
